import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { signInWithPopup } from 'firebase/auth';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { auth, appleProvider, db, googleProvider } from '../firebase';
import { useAuth } from '../AuthContext';
import { Logo } from '../components/UI';
import ArtistProfileForm from '../components/ArtistProfileForm';

async function ensureUser(user, provider) {
  const userRef = doc(db, 'users', user.uid);
  const { getDoc } = await import('firebase/firestore');
  const snapshot = await getDoc(userRef);
  if (!snapshot.exists() || snapshot.data().deleted) {
    await setDoc(userRef, {
      userId: user.uid,
      email: user.email || null,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      authProvider: provider,
      artist_area: true,
      area_member: true,
      requiresMembershipCode: false,
      createdAt: new Date().toISOString(),
    });
  } else if (snapshot.data().deactivated) {
    await updateDoc(userRef, { deactivated: false, deactivatedAt: null, reactivatedAt: new Date().toISOString() });
  }
}

export function LoginPage() {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const login = async (provider, name) => {
    setBusy(name); setError('');
    try {
      const result = await signInWithPopup(auth, provider);
      await ensureUser(result.user, name);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') setError(err.message || 'Sign in failed.');
    } finally { setBusy(''); }
  };
  return <div className="auth-page"><header className="mobile-auth-header"><Link to="/" className="mobile-header-logo" aria-label="Area home"><Logo compact /></Link></header><section className="auth-visual"><Logo /><div><h1>Your area.<br /><em>Your people.</em></h1></div></section><section className="auth-panel"><div className="auth-card"><h2>Sign in</h2><button className="oauth-button" disabled={!!busy} onClick={() => login(googleProvider, 'google')}><span className="google-g">G</span>{busy === 'google' ? 'Connecting…' : 'Continue with Google'}</button><button className="oauth-button apple" disabled={!!busy} onClick={() => login(appleProvider, 'apple')}><span className="apple-icon">●</span>{busy === 'apple' ? 'Connecting…' : 'Continue with Apple'}</button>{error && <p className="form-error">{error}</p>}<p className="legal">By continuing, you agree to our <Link to="/terms">Terms & Privacy Policy</Link>.</p></div></section></div>;
}

export function ProfileFormPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  return <main className="center-page"><div className="form-card"><Logo compact /><h1>{profile?.username ? 'Complete profile' : 'Create profile'}</h1><ArtistProfileForm onboarding onSaved={() => navigate('/', { replace: true })} /></div></main>;
}

export function TermsPage() {
  return <main className="center-page"><article className="terms-card"><Link className="back-link" to="/login"><ArrowLeft size={17} /> Back</Link><p className="eyebrow">AREA</p><h1>Terms & Privacy</h1><h2>Connect with care.</h2><p>By using Area, you agree to treat everyone respectfully, share only content you have the right to share, and protect the privacy of community members.</p><h2>Your information.</h2><p>Your account, profile, professions, skills, location, posts, and conversations are stored using Firebase services to provide the community experience. Your profile and posts are visible to others; direct messages are limited to their participants. Profile coordinates center nearby discovery. Posts store their selected location and coordinates publicly for distance filtering, even if you later change your profile location. Location search text is sent to the configured Photon service to find OpenStreetMap locations.</p><h2>Location data credits.</h2><p>Location data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>, available under the Open Database License.</p><h2>Your choices.</h2><p>You can update your profile, choose a different browsing or posting location, block people, and sign out at any time. Never share private or sensitive information in public posts or community circles.</p></article></main>;
}
